import { z } from 'zod';
import { PlayStatus } from '../enums';

/**
 * How a tie on score was resolved (engine Standing.tiebreak). Rows carry it only when it is not
 * NONE. MANUAL = the admin's playoff order (PUT manual-order); `playoffHoles` are the holes played
 * in that playoff when the admin recorded them.
 */
export const LeaderboardTiebreakSchema = z.object({
  by: z.enum([
    'COUNTBACK',
    'HOLE_BY_HOLE',
    'LOWER_HANDICAP',
    'SHARED',
    'PLAYOFF_REQUIRED',
    'MANUAL',
  ]),
  /** COUNTBACK: the segment that separated the tie (9, 6, 3, 1) */
  lastHoles: z.number().optional(),
  /** HOLE_BY_HOLE: the card hole that separated the tie */
  hole: z.number().optional(),
  /** MANUAL: holes of the playoff in playing order (e.g. [18, 18, 1]) */
  playoffHoles: z.array(z.number()).optional(),
});
export type LeaderboardTiebreak = z.infer<typeof LeaderboardTiebreakSchema>;

/** Explanation shown for a decided tie: PLAYOFF (manual order), COUNTBACK (incl. hole by hole), HANDICAP. */
export type TiebreakNoteKind = 'PLAYOFF' | 'COUNTBACK' | 'HANDICAP';

export function tiebreakNoteKind(
  t: LeaderboardTiebreak | null | undefined,
): TiebreakNoteKind | null {
  switch (t?.by) {
    case 'MANUAL':
      return 'PLAYOFF';
    case 'COUNTBACK':
    case 'HOLE_BY_HOLE':
      return 'COUNTBACK';
    case 'LOWER_HANDICAP':
      return 'HANDICAP';
    default:
      return null;
  }
}

/** One decided tie of a ranked view: positions `from`..`to` were level on score. */
export interface TiebreakBlock {
  kind: TiebreakNoteKind;
  from: number;
  to: number;
  /** PLAYOFF only, when recorded */
  playoffHoles: number[];
  competitorIds: string[];
}

type TiebreakRow = {
  competitorId: string;
  position: number | null;
  primary: number | null;
  toPar: number | null;
  tiebreak?: LeaderboardTiebreak | undefined;
};

/**
 * Decided ties of a view in display order (results PDF / XLSX footnote "1–2위 플레이오프 · 3–4위
 * 백카운트"). A tie group = consecutive ranked rows level on the engine's sort key (To Par when
 * every ranked row has one, else primary — engine #73). Inside a group, every run of consecutive
 * rows decided the same way (PLAYOFF, COUNTBACK, HANDICAP) is one block, so a group settled partly
 * by playoff and partly by countback gets two blocks, and rows still sharing a position after an
 * exhausted countback ("T5", tiebreak SHARED) are left out — the label already says it (five
 * players on 142 where countback only drops one to 9th → block 9–9, the four T5 rows unmarked;
 * the same rows the XLSX tie-break column fills).
 */
export function tiebreakBlocks(rows: readonly TiebreakRow[]): TiebreakBlock[] {
  const ranked = rows.filter((r) => r.position !== null);
  const useToPar = ranked.length > 0 && ranked.every((r) => r.toPar !== null);
  const key = (r: TiebreakRow) => (useToPar ? r.toPar : r.primary);
  const out: TiebreakBlock[] = [];
  let i = 0;
  while (i < ranked.length) {
    let j = i + 1;
    while (j < ranked.length && key(ranked[j]!) === key(ranked[i]!)) j++;
    const group = ranked.slice(i, j);
    i = j;
    if (group.length < 2) continue;
    out.push(...decidedRuns(group));
  }
  return out;
}

/**
 * Note kind of each row of a tie group. A row with an explicit undecided resolution (SHARED,
 * PLAYOFF_REQUIRED) has none and splits runs; a row without any tiebreak (NONE is omitted on the
 * wire, older snapshots, hand-built rows) takes the kind of the nearest row before it that has
 * one, else after it, so it never cuts a decided pair in two.
 */
function groupKinds(group: readonly TiebreakRow[]): Array<TiebreakNoteKind | null> {
  const own = group.map((r) => (r.tiebreak ? tiebreakNoteKind(r.tiebreak) : undefined));
  return own.map((k, idx) => {
    if (k !== undefined) return k;
    for (let p = idx - 1; p >= 0; p--) if (own[p] !== undefined) return own[p]!;
    for (let n = idx + 1; n < own.length; n++) if (own[n] !== undefined) return own[n]!;
    return null;
  });
}

/** Runs of consecutive rows of one tie group decided the same way (undecided rows split runs). */
function decidedRuns(group: readonly TiebreakRow[]): TiebreakBlock[] {
  const kinds = groupKinds(group);
  const out: TiebreakBlock[] = [];
  let k = 0;
  while (k < group.length) {
    const kind = kinds[k]!;
    let m = k + 1;
    while (m < group.length && kinds[m] === kind) m++;
    const run = group.slice(k, m);
    k = m;
    if (!kind) continue;
    const holes =
      kind === 'PLAYOFF'
        ? (run.find((r) => r.tiebreak?.playoffHoles?.length)?.tiebreak?.playoffHoles ?? [])
        : [];
    out.push({
      kind,
      from: run[0]!.position!,
      to: run[run.length - 1]!.position!,
      playoffHoles: [...holes],
      competitorIds: run.map((r) => r.competitorId),
    });
  }
  return out;
}

/**
 * Number of separate ties (score levels, same key as `tiebreakBlocks`) the given competitors
 * fall into. Playoff holes are stored once per view (`playoff:<view>`), so they may only be
 * recorded when one playoff block is ordered (manual-order 400 PLAYOFF_HOLES_AMBIGUOUS).
 */
export function playoffBlockCount(
  rows: readonly TiebreakRow[],
  competitorIds: readonly string[],
): number {
  const ranked = rows.filter((r) => r.position !== null);
  const useToPar = ranked.length > 0 && ranked.every((r) => r.toPar !== null);
  const wanted = new Set(competitorIds);
  const levels = new Set<number | null>();
  for (const r of rows) {
    if (wanted.has(r.competitorId)) levels.add(useToPar ? r.toPar : r.primary);
  }
  return levels.size;
}

/** One leaderboard row as sent to browsers (public + admin). */
export const LeaderboardRowSchema = z.object({
  competitorId: z.string(), // participantId or teamId
  kind: z.enum(['INDIVIDUAL', 'TEAM']),
  name: z.string(),
  bib: z.number().nullable(),
  memberNames: z.array(z.string()).optional(),
  nationality: z.string().nullable().optional(),
  categories: z.record(z.string(), z.string()),
  status: PlayStatus,
  position: z.number().nullable(),
  positionLabel: z.string(), // "1", "T3", "WD", "-"
  tied: z.boolean(),
  rank: z.number(),
  /** previous position for movement arrows/animation (null = new) */
  previousPosition: z.number().nullable(),
  primary: z.number().nullable(),
  gross: z.number().nullable(),
  net: z.number().nullable(),
  points: z.number().nullable(),
  toPar: z.number().nullable(),
  thru: z.union([z.number(), z.literal('F')]).nullable(),
  startHole: z.number().nullable(),
  teeTime: z.string().nullable(),
  currentRound: z.number().nullable(),
  /** layout of the current round's group (multi-layout rounds) */
  courseId: z.uuid().nullable().optional(),
  /** public views: null for NEW_PERIO/PERIO until the round's hidden holes are revealed */
  handicap: z.number().nullable(),
  /** holes skipped before the last entered hole in the current round (admin views; public omits) */
  missingHoles: z.array(z.number()).optional(),
  rounds: z.array(
    z.object({
      roundNo: z.number(),
      gross: z.number().nullable(),
      net: z.number().nullable(),
      points: z.number().nullable(),
      toPar: z.number().nullable(),
      holesPlayed: z.number(),
    }),
  ),
  needsPlayoff: z.boolean(),
  /** how a tie on score was resolved; absent when not tied (engine NONE) and on older snapshots */
  tiebreak: LeaderboardTiebreakSchema.optional(),
  /**
   * the current round's card is complete and signed by the player (= submitted, §15 #475);
   * absent otherwise (DISPUTED / INVALIDATED / not signed / team rows / older servers)
   */
  submitted: z.literal(true).optional(),
});
export type LeaderboardRowDto = z.infer<typeof LeaderboardRowSchema>;

/**
 * Compact position-only update in leaderboard.patch (id = competitorId, p = position,
 * l = positionLabel, t = tied, r = rank, pp = previousPosition).
 */
export const LeaderboardMoveSchema = z.object({
  id: z.string(),
  p: z.number().nullable(),
  l: z.string(),
  t: z.boolean(),
  r: z.number(),
  pp: z.number().nullable(),
});
export type LeaderboardMoveDto = z.infer<typeof LeaderboardMoveSchema>;

export const LeaderboardViewSchema = z.object({
  tournamentId: z.uuid(),
  /**
   * Stream key of this view: `<audience prefix><view>[|r<roundNo>]`, public views are prefixed
   * `pub:` (hidden-hole/Perio redaction applied BEFORE ranking), admin views `adm:`.
   * Filtered/category views are derived on request and never streamed.
   */
  viewKey: z.string(),
  /** monotonic realtime version this view reflects */
  version: z.number(),
  view: z.string(), // FormatKind or ViewKind
  direction: z.enum(['LOWER_BETTER', 'HIGHER_BETTER']),
  phase: z.enum(['LIVE', 'FINAL']),
  roundNo: z.number().nullable(),
  categoryFilter: z.record(z.string(), z.array(z.string())).nullable(),
  generatedAt: z.iso.datetime(),
  rows: z.array(LeaderboardRowSchema),
});
export type LeaderboardView = z.infer<typeof LeaderboardViewSchema>;

export const LeaderboardQuerySchema = z.object({
  view: z.string().optional(),
  round: z.coerce.number().int().min(1).optional(),
  /** repeated `cat=key:value` pairs, AND across keys, OR within the same key */
  cat: z.union([z.string(), z.array(z.string())]).optional(),
  phase: z.enum(['LIVE', 'FINAL']).optional(),
});

/** Hole-by-hole scorecard (선수별 홀 스코어카드). */
export const ScorecardSchema = z.object({
  tournamentId: z.uuid(),
  competitorId: z.string(),
  name: z.string(),
  bib: z.number().nullable(),
  teeName: z.string().nullable(),
  handicap: z.number().nullable(),
  rounds: z.array(
    z.object({
      roundNo: z.number(),
      roundId: z.uuid(),
      status: z.string(),
      holes: z.array(
        z.object({
          number: z.number(),
          par: z.number(),
          strokeIndex: z.number(),
          distanceMeters: z.number().nullable(),
          strokes: z.number().nullable(),
          putts: z.number().nullable(),
          /** strokes - par (null = not played): drives birdie/bogey colours */
          diff: z.number().nullable(),
          points: z.number().nullable(),
          handicapStrokes: z.number(),
          pickedUp: z.boolean(),
        }),
      ),
      out: z.number().nullable(),
      in: z.number().nullable(),
      total: z.number().nullable(),
      toPar: z.number().nullable(),
      courseId: z.uuid(),
      courseName: z.string(),
      signOff: z
        .object({
          status: z.enum(['SIGNED', 'DISPUTED', 'INVALIDATED']),
          method: z.enum(['PLAYER_PORTAL', 'CADDY_DEVICE', 'ADMIN']),
          signedAt: z.iso.datetime(),
        })
        .nullable(),
    }),
  ),
});
export type Scorecard = z.infer<typeof ScorecardSchema>;

/**
 * GET /public/t/:token/holes?round= — every competitor's hole scores of one round (TV hole board,
 * §15 #476), from the same redacted public data as the leaderboard (hidden last holes absent).
 */
export const PublicHoleBoardSchema = z.object({
  round: z.number().nullable(),
  /** card holes of the round's main layout (headers); rows may play another layout */
  holes: z.array(z.object({ number: z.number(), par: z.number() })),
  rows: z.array(
    z.object({
      competitorId: z.string(),
      holes: z.array(
        z.object({
          hole: z.number(),
          par: z.number(),
          strokes: z.number().nullable(),
          /** strokes − par (null = not played or picked up) */
          diff: z.number().nullable(),
          pickedUp: z.boolean(),
        }),
      ),
      out: z.number().nullable(),
      in: z.number().nullable(),
      total: z.number().nullable(),
      toPar: z.number().nullable(),
    }),
  ),
});
export type PublicHoleBoard = z.infer<typeof PublicHoleBoardSchema>;

/**
 * One leader block per (contest, round): a contest held in every round (SpecialContest.roundId
 * null) yields one block per round. Ties: earlier server createdAt wins.
 */
export const ContestLeaderSchema = z.object({
  contestId: z.uuid(),
  type: z.enum(['NEAREST_PIN', 'LONGEST_DRIVE']),
  holeNumber: z.number(),
  courseId: z.uuid().nullable(),
  roundId: z.uuid(),
  roundNo: z.number(),
  name: z.string().nullable(),
  leader: z
    .object({
      participantId: z.uuid(),
      name: z.string(),
      bib: z.number(),
      distanceMeters: z.number(),
      measuredAt: z.iso.datetime(),
    })
    .nullable(),
  /** top entries (VALID only, best first) */
  top: z.array(
    z.object({
      entryId: z.uuid(),
      participantId: z.uuid(),
      name: z.string(),
      bib: z.number(),
      distanceMeters: z.number(),
      measuredAt: z.iso.datetime(),
    }),
  ),
  entryCount: z.number(),
});
export type ContestLeader = z.infer<typeof ContestLeaderSchema>;

/** One player of a match side in the handicap strokes panel (G24). */
export const MatchHandicapPlayerSchema = z.object({
  id: z.string(),
  name: z.string(),
  handicapIndex: z.number().nullable(),
  /** course handicap of the match round, rounded for display; null without index / rating */
  courseHandicap: z.number().nullable(),
  /**
   * course handicap before rounding, 1 decimal (e.g. 5.4): the allowance is applied to the
   * unrounded difference, so "7 vs 13" alone cannot explain the strokes. null like courseHandicap;
   * absent on older servers.
   */
  courseHandicapExact: z.number().nullable().optional(),
  /** strokes this player receives: singles = the side's, fourball = own; null = shared ball (foursome) */
  strokes: z.number().nullable(),
});
export type MatchHandicapPlayer = z.infer<typeof MatchHandicapPlayerSchema>;

export const MatchHandicapSideSchema = z.object({
  players: z.array(MatchHandicapPlayerSchema),
  /** total strokes the side receives over the round (fourball: the most any partner gets per hole) */
  strokes: z.number(),
  /** card holes where the side receives strokes, in card order (stroke index = hole handicap) */
  holes: z.array(z.object({ hole: z.number(), strokes: z.number(), strokeIndex: z.number() })),
});
export type MatchHandicapSide = z.infer<typeof MatchHandicapSideSchema>;

/** Handicap strokes of a match: per side index, course handicap, strokes received and +1 holes. */
export const MatchHandicapSchema = z.object({
  format: z.enum(['MATCH_SINGLE', 'MATCH_FOURSOME', 'MATCH_FOURBALL']),
  /** false = scratch match (nobody receives strokes) */
  net: z.boolean(),
  /** format allowance applied once (singles 1, fourball 0.9, foursome 0.5 of the difference) */
  allowance: z.number(),
  A: MatchHandicapSideSchema,
  B: MatchHandicapSideSchema,
});
export type MatchHandicap = z.infer<typeof MatchHandicapSchema>;

/**
 * One side of a match view. seed = 1-based position in Bracket.seeds (null outside a bracket or
 * for a TBD side), nationality = Participant.nationality (null for teams) — both already public
 * (bracket seeding, leaderboard rows); optional for older servers.
 */
export const MatchViewSideSchema = z.object({
  id: z.string().nullable(),
  name: z.string(),
  seed: z.number().int().nullable().optional(),
  nationality: z.string().nullable().optional(),
});
export type MatchViewSide = z.infer<typeof MatchViewSideSchema>;

export const MatchViewSchema = z.object({
  matchId: z.uuid(),
  bracketId: z.uuid().nullable(),
  bracketRound: z.number().nullable(),
  bracketIndex: z.number().nullable(),
  roundNo: z.number().nullable(),
  status: z.enum(['SCHEDULED', 'IN_PROGRESS', 'COMPLETE']),
  sideA: MatchViewSideSchema,
  sideB: MatchViewSideSchema,
  leader: z.enum(['A', 'B']).nullable(),
  up: z.number(),
  thru: z.number(),
  statusCode: z.enum(['NOT_STARTED', 'AS', 'UP', 'DORMIE', 'WON', 'HALVED']),
  resultText: z.string().nullable(),
  winner: z.enum(['A', 'B', 'HALVED']).nullable(),
  holes: z.array(z.object({ hole: z.number(), winner: z.enum(['A', 'B', 'HALVED']).nullable() })),
  /**
   * Knockout (bracket) match all square after the regulation holes: extra holes in progress until
   * an extra hole or an admin decision settles it — never halved. statusCode stays 'AS' (thru = holes
   * played) so clients without this field still read "all square"; optional for older servers.
   */
  playoff: z.boolean().optional(),
  /** handicap strokes per side (null when the sides are not known / no round); optional for older servers */
  handicap: MatchHandicapSchema.nullable().optional(),
});
export type MatchView = z.infer<typeof MatchViewSchema>;
